import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Scale } from "lucide-react";

const FIELDS = [
  { key: "min_standard_rate", label: "Minimum standard hourly rate (£)" },
  { key: "suggested_standard_min", label: "Suggested standard rate — minimum (£)" },
  { key: "suggested_standard_max", label: "Suggested standard rate — maximum (£)" },
  { key: "min_priority_rate", label: "Minimum priority hourly rate (£)" },
  { key: "suggested_priority_min", label: "Suggested priority rate — minimum (£)" },
  { key: "suggested_priority_max", label: "Suggested priority rate — maximum (£)" },
  { key: "max_priority_rate", label: "Maximum realistic priority rate (£)" },
];

export default function AdminPricingTab() {
  const [values, setValues] = useState(null);
  const [regions, setRegions] = useState({});
  const [region, setRegion] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async (r = "") => {
    const { data } = await api.get("/admin/pricing");
    setRegions(data.regions || {});
    setNote(data.advisory_note);
    setValues({ ...data.default, ...((data.regions || {})[r] || {}) });
  };
  useEffect(() => { load(region); /* eslint-disable-next-line */ }, [region]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/pricing", { region: region || null, values });
      setRegions(data.regions || {});
      toast.success(region ? `Saved guidance for ${region}` : "Saved default pricing guidance");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!values) return <Loader2 className="h-6 w-6 animate-spin text-accent" />;

  return (
    <div className="space-y-5 max-w-2xl" data-testid="admin-pricing">
      <div>
        <h3 className="font-display font-semibold flex items-center gap-2"><Scale className="h-5 w-5 text-accent" /> Pricing guidance</h3>
        <p className="text-sm text-muted-foreground mt-0.5">{note}</p>
      </div>

      <div className="bg-card rounded-2xl border border-primary/5 p-5 space-y-4">
        <label className="text-sm block">Region (optional — leave blank for the platform default)
          <input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="e.g. london" className="ap-input" data-testid="pricing-region" />
          {Object.keys(regions).length > 0 && (
            <span className="text-xs text-muted-foreground">Configured regions: {Object.keys(regions).join(", ")}</span>
          )}
        </label>
        <div className="grid sm:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <label key={f.key} className="text-sm">{f.label}
              <input type="number" step="0.5" min="0" value={values[f.key] ?? ""}
                onChange={(e) => setValues({ ...values, [f.key]: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                className="ap-input" data-testid={`pricing-${f.key}`} />
            </label>
          ))}
        </div>
        <button onClick={save} disabled={saving} className="px-6 py-3 rounded-full bg-accent text-white font-medium inline-flex items-center gap-2 disabled:opacity-50" data-testid="pricing-save-btn">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save guidance
        </button>
      </div>

      <p className="text-xs text-muted-foreground">
        Guidance is advisory. Cleaners are never forced to match each other, are never shown what
        another individual cleaner charges, and can always price above the suggested range. The
        minimum floor only blocks clearly unrealistic pricing.
      </p>
      <style>{`.ap-input{width:100%;margin-top:0.25rem;padding:0.625rem 0.75rem;border-radius:0.75rem;border:1px solid hsl(var(--border));background:hsl(var(--background));outline:none}.ap-input:focus{border-color:hsl(var(--accent))}`}</style>
    </div>
  );
}
